import React from 'react';
import { Box, Paper, Typography, Button, Alert } from '@mui/material';
import frontendLogger from '../utils/frontendLogger';

interface Props {
  children: React.ReactNode;
}

interface State {
  error: Error | null;
}

/**
 * Rete di sicurezza per gli errori di render.
 *
 * Le rotte sono caricate con React.lazy: dopo un rilascio, un chunk non piu'
 * disponibile fa fallire l'import e senza questo confine l'utente resta davanti
 * a una pagina completamente bianca, senza nemmeno un modo per ricaricare.
 */
class ErrorBoundary extends React.Component<Props, State> {
  state: State = { error: null };

  static getDerivedStateFromError(error: Error): State {
    return { error };
  }

  componentDidCatch(error: Error, info: React.ErrorInfo) {
    frontendLogger.error('Render error', { message: error.message, stack: info.componentStack });
  }

  private handleReload = () => {
    // ricaricare risolve il caso piu' comune: chunk vecchio dopo un rilascio
    window.location.reload();
  };

  render() {
    if (!this.state.error) return this.props.children;

    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', mt: 8, px: 2 }}>
        <Paper sx={{ p: 4, maxWidth: 520, width: '100%' }}>
          <Typography variant="h6" gutterBottom>
            Qualcosa è andato storto
          </Typography>
          <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
            La pagina non è riuscita a caricarsi. Se hai appena visto un aggiornamento
            dell'applicazione, di solito basta ricaricare.
          </Typography>

          {process.env.NODE_ENV !== 'production' && (
            <Alert severity="error" sx={{ mb: 2, whiteSpace: 'pre-wrap' }}>
              {this.state.error.message}
            </Alert>
          )}

          <Button variant="contained" onClick={this.handleReload}>
            Ricarica la pagina
          </Button>
        </Paper>
      </Box>
    );
  }
}

export default ErrorBoundary;
